import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import classNames from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/Store";
import {
  addInteractionCopy,
  selectInteractionRange,
  selectSelectedElementId,
  selectSelectedInteractionIds,
  selectSelectedInteractions,
  setSelectedInteraction,
  toggleInteractionSelection,
  updateSelectedInteraction,
} from "../../app/slices/TrainingSlice";
import {
  selectErrorsForInteraction,
  selectWarningsForInteraction,
} from "../../app/slices/ValidationSlice";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import { useDeleteInteraction } from "../../hooks/DeleteInteractionHook";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { AllInteractions } from "../interaction_picker/InteractionDefinitions";
import { useInteractionPickerIcons } from "../interaction_picker/InteractionPickerIconHooks";
import PiMenuButton from "../menu/PiMenuButton";
import { useCopyInteraction } from "../training_timeline/CopyInteractionHook";
import { useDuplicateInteractions } from "../training_timeline/useDuplicateInteractions";
import {
  CopyPasteContext,
  TimelineClipboardContext,
} from "../training_timeline/CopyPasteContext";
import { EditorColumnRow } from "./EditorColumn";
import {
  getFeedbackInteractionDndId,
  getInteractionDndId,
  TakeInteractionDndData,
} from "../training_timeline/TakeInteractionDndProvider";
import { QuizFeedbackSlot } from "../../models/Quiz";

interface EditorInteractionRowProps {
  interaction: Interaction;
  ghost?: boolean;
  testIdPrefix: string;
}

interface EditorInteractionRowViewProps extends EditorInteractionRowProps {
  nodeRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  dragAttributes?: any;
  dragListeners?: any;
}

function InteractionIssueBadge(props: {
  warningCount?: number;
  errorCount?: number;
}) {
  const { warningCount = 0, errorCount = 0 } = props;

  if (errorCount > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold flex-shrink-0 text-creator-error bg-creator-error-washed">
        <ErrorOutlineOutlinedIcon fontSize="inherit" />
        {errorCount}
      </span>
    );
  }

  if (warningCount > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold flex-shrink-0 text-[var(--pi-key-yellow)] bg-[var(--pi-key-yellow-faded)]">
        <WarningAmberOutlinedIcon fontSize="inherit" />
        {warningCount}
      </span>
    );
  }

  return null;
}

export function EditorInteractionRowView(props: EditorInteractionRowViewProps) {
  const { interaction, ghost, testIdPrefix } = props;
  const dispatch = useDispatch();
  const copyPasteContext = useContext(CopyPasteContext);
  const clipboardActions = useContext(TimelineClipboardContext);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const selectedElementId = useSelector(selectSelectedElementId);
  const [getIcon] = useInteractionPickerIcons();
  const [deleteInteraction, deleteInteractions] = useDeleteInteraction();
  const { getInteractionCopy } = useCopyInteraction();
  const duplicateInteractions = useDuplicateInteractions();
  const selectedInteractions = useSelector(selectSelectedInteractions);
  const warnings = useSelector((state: RootState) =>
    selectWarningsForInteraction(state, interaction.id)
  );
  const errors = useSelector((state: RootState) =>
    selectErrorsForInteraction(state, interaction.id)
  );
  const interactionDefinition = AllInteractions.find(
    (definition) => definition.type === interaction.type
  );
  const icon = interactionDefinition ? (
    getIcon(interactionDefinition)
  ) : (
    <MessageOutlinedIcon fontSize="inherit" />
  );
  const isSelected = selectedElementId === interaction.id;
  const isInMultiSelection = useSelector((state: RootState) =>
    selectSelectedInteractionIds(state).includes(interaction.id)
  );
  const selectionCount = useSelector(
    (state: RootState) => selectSelectedInteractionIds(state).length
  );
  const pendingCut = copyPasteContext?.pendingCut;
  const isPendingCut =
    (pendingCut?.kind === "timelineInteraction" &&
      pendingCut.interactionId === interaction.id) ||
    (pendingCut?.kind === "timelineInteractions" &&
      pendingCut.interactionIds.includes(interaction.id));
  const isGroupTarget = isInMultiSelection && selectionCount > 1;
  const isDisabled = Boolean(interaction.disabled);

  useEffect(() => {
    if (isRenaming) inputRef.current?.focus();
  }, [isRenaming]);

  useEffect(() => {
    setIsRenaming(false);
  }, [selectedElementId]);

  const selectInteraction = () => {
    dispatch(setSelectedInteraction(interaction.id));
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.shiftKey) {
      dispatch(selectInteractionRange(interaction.id));
    } else if (event.ctrlKey || event.metaKey) {
      dispatch(toggleInteractionSelection(interaction.id));
    } else {
      selectInteraction();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    selectInteraction();
  };

  const stopRowInteraction = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const startRenameInteraction = () => {
    selectInteraction();
    setIsRenaming(true);
  };

  const duplicateInteraction = () => {
    if (isGroupTarget) {
      duplicateInteractions(selectedInteractions);
      return;
    }
    selectInteraction();
    const copy = getInteractionCopy(interaction);
    if (copy) dispatch(addInteractionCopy(copy));
  };

  const removeInteraction = () => {
    if (isGroupTarget) {
      deleteInteractions(selectedInteractions);
      return;
    }
    selectInteraction();
    deleteInteraction(interaction);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "NumpadEnter") {
      setIsRenaming(false);
    }
  };

  const title = isRenaming ? (
    <input
      ref={inputRef}
      className="w-full min-w-0 rounded-5 border border-creator-border bg-creator-surface px-1 text-[13px] font-medium leading-5 text-creator-text outline-none"
      value={interaction.name}
      onBlur={() => setIsRenaming(false)}
      onChange={(event) =>
        dispatch(updateSelectedInteraction({ ...interaction, name: event.target.value }))
      }
      onClick={stopRowInteraction}
      onContextMenu={stopRowInteraction}
      onKeyDown={onInputKeyDown}
    />
  ) : (
    interaction.name
  );

  // Opening the row menu focuses the row (keeping an existing group intact)
  // so the clipboard entries act on what the user sees highlighted.
  const focusRowForMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!isInMultiSelection && !isSelected) selectInteraction();
  };

  const menu = !ghost && (
    <span
      className="my-[-4px] flex-shrink-0"
      onClick={focusRowForMenu}
      onContextMenu={stopRowInteraction}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <PiPopoverMenu softIconHover modernMenu>
        <PiMenuButton
          text={t("rename")}
          icon={<EditOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={startRenameInteraction}
          disabled={interaction.type === InteractionType.DynamicInteraction}
          variant="modern"
        />
        <PiMenuButton
          text={
            isGroupTarget
              ? `${t("duplicate")} ${selectionCount}`
              : t("duplicate")
          }
          icon={<ContentCopyOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={duplicateInteraction}
          variant="modern"
        />
        {clipboardActions && (
          <PiMenuButton
            text={isGroupTarget ? `${t("copy")} ${selectionCount}` : t("copy")}
            icon={
              <ContentCopyOutlinedIcon color="inherit" fontSize="inherit" />
            }
            onClick={() => clipboardActions.handleCopy()}
            variant="modern"
          />
        )}
        {clipboardActions && (
          <PiMenuButton
            text={isGroupTarget ? `${t("cut")} ${selectionCount}` : t("cut")}
            icon={<ContentCutOutlinedIcon color="inherit" fontSize="inherit" />}
            onClick={() => clipboardActions.handleCut()}
            variant="modern"
          />
        )}
        {clipboardActions && (
          <PiMenuButton
            text={t("paste")}
            icon={
              <ContentPasteOutlinedIcon color="inherit" fontSize="inherit" />
            }
            onClick={() => void clipboardActions.handlePaste()}
            variant="modern"
          />
        )}
        <PiMenuButton
          text={isGroupTarget ? `${t("delete")} ${selectionCount}` : t("delete")}
          icon={<DeleteOutlineOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={removeInteraction}
          variant="modern"
          tone="danger"
        />
      </PiPopoverMenu>
    </span>
  );

  return (
    <div ref={props.nodeRef} style={props.style}>
      <EditorColumnRow
        variant="flat"
        icon={icon}
        title={title}
        titleClassName="font-medium"
        trailing={
          <>
            {isPendingCut && (
              <span
                className="flex flex-shrink-0 text-[16px] text-creator-text-sub"
                aria-hidden="true"
              >
                <ContentCutOutlinedIcon fontSize="inherit" />
              </span>
            )}
            {isDisabled && (
              <span
                className="flex flex-shrink-0 text-[16px] text-creator-text-sub"
                aria-label={t("disabled") as string}
                data-testid="interaction-row-disabled-badge"
              >
                <VisibilityOffOutlinedIcon fontSize="inherit" />
              </span>
            )}
            {!isDisabled && (
              <InteractionIssueBadge
                warningCount={warnings?.length ?? 0}
                errorCount={errors?.length ?? 0}
              />
            )}
            {menu}
          </>
        }
        selected={isSelected}
        softSelected={isInMultiSelection && !isSelected}
        ghost={ghost}
        className={
          classNames({
            "opacity-60 transition-opacity duration-150 motion-reduce:transition-none":
              isPendingCut,
            "opacity-50": !isPendingCut && isDisabled,
          }) || undefined
        }
        ariaLabel={
          isPendingCut
            ? `${interaction.name}, cut pending. Paste to move or press Escape to cancel.`
            : isInMultiSelection && selectionCount > 1
            ? `${interaction.name}, selected. ${selectionCount} interactions selected.`
            : isDisabled
            ? `${interaction.name}, ${t("disabled")}`
            : undefined
        }
        onClick={handleRowClick}
        onKeyDown={handleKeyDown}
        dragAttributes={props.dragAttributes}
        dragListeners={props.dragListeners}
        dataAttributes={{
          "data-interaction-id": interaction.id,
          "data-pending-cut": isPendingCut ? "true" : undefined,
          "data-disabled": isDisabled ? "true" : undefined,
        }}
        testId={`${testIdPrefix}-${interaction.id}`}
      />
    </div>
  );
}

export interface EditorInteractionRowFeedbackContext {
  takeId: string;
  quizId: string;
  slot: QuizFeedbackSlot;
}

export default function EditorInteractionRow(
  props: EditorInteractionRowProps & {
    takeId?: string;
    feedbackContext?: EditorInteractionRowFeedbackContext;
  }
) {
  const { interaction, takeId, feedbackContext } = props;
  const dragData: TakeInteractionDndData | undefined = feedbackContext
    ? {
        kind: "feedback-interaction",
        takeId: feedbackContext.takeId,
        quizId: feedbackContext.quizId,
        slot: feedbackContext.slot,
        interactionId: interaction.id,
      }
    : takeId
      ? { kind: "interaction", takeId, interactionId: interaction.id }
      : undefined;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: feedbackContext
        ? getFeedbackInteractionDndId(interaction.id)
        : takeId
          ? getInteractionDndId(interaction.id)
          : interaction.id,
      data: dragData,
      transition: {
        duration: 150,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      },
    });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <EditorInteractionRowView
      {...props}
      nodeRef={setNodeRef}
      style={style}
      dragAttributes={attributes}
      dragListeners={listeners}
    />
  );
}
